import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Receipt, 
  Plus, 
  Search,
  DollarSign,
  MoreVertical,
  Pencil,
  Trash2,
  Send,
  CheckCircle,
  FileText,
  Download,
  Eye,
  Printer,
  Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import DocumentViewer from '../components/DocumentViewer';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { useSettings } from '../context/SettingsContext';
import SARSymbol from '../components/SARSymbol';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency display helper
const CurrencyValue = ({ value, symbol, isSAR, size = 16 }) => {
  if (isSAR) {
    return (
      <span className="inline-flex items-center gap-1">
        <SARSymbol size={size} />
        <span>{value}</span>
      </span>
    );
  }
  return <span>{symbol} {value}</span>;
};

const Invoices = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol, formatCurrency, isSAR } = useSettings();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [formData, setFormData] = useState({ items: [] });
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [template, setTemplate] = useState(null);

  const statusColors = {
    draft: 'bg-gray-50 text-gray-700 border-gray-200',
    sent: 'bg-blue-50 text-blue-700 border-blue-200',
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    partial: 'bg-amber-50 text-amber-700 border-amber-200',
    overdue: 'bg-red-50 text-red-700 border-red-200',
    cancelled: 'bg-slate-50 text-slate-700 border-slate-200'
  };

  const statusLabels = {
    draft: isRTL ? 'مسودة' : 'Draft',
    sent: isRTL ? 'مرسلة' : 'Sent',
    paid: isRTL ? 'مدفوعة' : 'Paid',
    partial: isRTL ? 'جزئي' : 'Partial',
    overdue: isRTL ? 'متأخرة' : 'Overdue',
    cancelled: isRTL ? 'ملغاة' : 'Cancelled'
  };

  useEffect(() => {
    fetchInvoices();
    fetchClients();
    fetchProjects();
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/templates/default?document_type=invoice`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(res.data);
    } catch (err) {
      console.log('No template found, using defaults');
    }
  };

  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pm/invoices`);
      setInvoices(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/clients`);
      setClients(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pm/projects`);
      setProjects(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      if (editingInvoice) {
        await axios.put(`${API_URL}/api/pm/invoices/${editingInvoice.id}`, formData);
        toast.success(isRTL ? 'تم تحديث الفاتورة' : 'Invoice updated');
      } else {
        await axios.post(`${API_URL}/api/pm/invoices`, formData);
        toast.success(isRTL ? 'تم إنشاء الفاتورة' : 'Invoice created');
      }
      setShowDialog(false);
      setEditingInvoice(null);
      setFormData({ items: [] });
      fetchInvoices();
    } catch (err) {
      toast.error(isRTL ? 'فشل حفظ الفاتورة' : 'Failed to save invoice');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(isRTL ? 'حذف هذه الفاتورة؟' : 'Delete this invoice?')) {
      try {
        await axios.delete(`${API_URL}/api/pm/invoices/${id}`);
        toast.success(isRTL ? 'تم حذف الفاتورة' : 'Invoice deleted');
        fetchInvoices();
      } catch (err) {
        toast.error(isRTL ? 'فشل الحذف' : 'Failed to delete');
      }
    }
  };

  const updateStatus = async (invoiceId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/pm/invoices/${invoiceId}`, { status: newStatus });
      toast.success(isRTL ? 'تم تحديث الحالة' : 'Status updated');
      fetchInvoices();
    } catch (err) {
      toast.error(isRTL ? 'فشل التحديث' : 'Failed to update');
    }
  };

  const openNew = () => {
    setEditingInvoice(null);
    setFormData({ items: [{ description: '', quantity: 1, unit_price: 0 }], tax_rate: 0 });
    setShowDialog(true);
  };

  const getClientName = (clientId) => clients.find(c => c.id === clientId)?.name || '-';

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...(formData.items || []), { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const calculateTotal = () => {
    const subtotal = (formData.items || []).reduce((sum, item) => 
      sum + (item.quantity || 0) * (item.unit_price || 0), 0
    );
    const tax = subtotal * ((formData.tax_rate || 0) / 100);
    return { subtotal, tax, total: subtotal + tax };
  };

  // Source labels for unified invoices
  const sourceLabels = {
    standalone: { label: isRTL ? 'مستقل' : 'Standalone', color: 'bg-gray-100 text-gray-700' },
    project: { label: isRTL ? 'مشروع' : 'Project', color: 'bg-blue-100 text-blue-700' },
    sales: { label: isRTL ? 'مبيعات' : 'Sales', color: 'bg-green-100 text-green-700' },
    pos: { label: isRTL ? 'نقطة بيع' : 'POS', color: 'bg-purple-100 text-purple-700' }
  };

  const filteredInvoices = invoices.filter(i => {
    const statusMatch = filterStatus === 'all' || i.status === filterStatus;
    const sourceMatch = filterSource === 'all' || (i.source || 'standalone') === filterSource;
    return statusMatch && sourceMatch;
  });

  const stats = {
    total: invoices.reduce((sum, i) => sum + (i.total || 0), 0),
    paid: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total || 0), 0),
    pending: invoices.filter(i => ['draft', 'sent', 'partial'].includes(i.status)).reduce((sum, i) => sum + (i.total || 0), 0),
    overdue: invoices.filter(i => i.status === 'overdue').reduce((sum, i) => sum + (i.total || 0), 0),
    // Source breakdown
    projectInvoices: invoices.filter(i => i.source === 'project').length,
    salesInvoices: invoices.filter(i => i.source === 'sales').length,
    standaloneInvoices: invoices.filter(i => !i.source || i.source === 'standalone').length
  };

  const totals = calculateTotal();

  return (
    <div className="space-y-6">
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-3xl font-bold tracking-tight">{t('pages.invoices.title')}</h1>
          <p className="text-muted-foreground">{t('pages.invoices.subtitle')}</p>
        </div>
        <Button onClick={openNew} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Plus className="h-4 w-4" /> {t('pages.invoices.createInvoice')}
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold"><CurrencyValue value={stats.total.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} /></div><div className="text-sm text-muted-foreground">{t('pages.common.total')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-emerald-600"><CurrencyValue value={stats.paid.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} /></div><div className="text-sm text-muted-foreground">{t('pages.invoices.paid')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-blue-600"><CurrencyValue value={stats.pending.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} /></div><div className="text-sm text-muted-foreground">{t('dashboard.pending')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-red-600"><CurrencyValue value={stats.overdue.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} /></div><div className="text-sm text-muted-foreground">{t('pages.invoices.overdue')}</div></CardContent></Card>
      </div>

      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue placeholder={t('pages.common.filter')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isRTL ? 'جميع الحالات' : 'All Status'}</SelectItem>
            <SelectItem value="draft">{statusLabels.draft}</SelectItem>
            <SelectItem value="sent">{statusLabels.sent}</SelectItem>
            <SelectItem value="paid">{statusLabels.paid}</SelectItem>
            <SelectItem value="partial">{statusLabels.partial}</SelectItem>
            <SelectItem value="overdue">{statusLabels.overdue}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="w-40"><SelectValue placeholder={isRTL ? 'المصدر' : 'Source'} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isRTL ? 'جميع المصادر' : 'All Sources'}</SelectItem>
            <SelectItem value="standalone">{sourceLabels.standalone.label}</SelectItem>
            <SelectItem value="project">{sourceLabels.project.label}</SelectItem>
            <SelectItem value="sales">{sourceLabels.sales.label}</SelectItem>
            <SelectItem value="pos">{sourceLabels.pos.label}</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground">
          {stats.projectInvoices > 0 && <Badge className="mr-2 bg-blue-100 text-blue-700">{stats.projectInvoices} {isRTL ? 'مشروع' : 'Project'}</Badge>}
          {stats.salesInvoices > 0 && <Badge className="mr-2 bg-green-100 text-green-700">{stats.salesInvoices} {isRTL ? 'مبيعات' : 'Sales'}</Badge>}
          {stats.standaloneInvoices > 0 && <Badge className="bg-gray-100 text-gray-700">{stats.standaloneInvoices} {isRTL ? 'مستقل' : 'Standalone'}</Badge>}
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.invoices.invoiceNumber')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.invoices.client')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'المصدر' : 'Source'}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'تاريخ الإصدار' : 'Issue Date'}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.invoices.dueDate')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.invoices.amount')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.invoices.status')}</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredInvoices.map((invoice) => {
              const source = invoice.source || 'standalone';
              return (
              <TableRow key={invoice.id}>
                <TableCell className={`font-mono font-medium ${isRTL ? 'text-right' : ''}`}>{invoice.invoice_number}</TableCell>
                <TableCell className={isRTL ? 'text-right' : ''}>{getClientName(invoice.client_id)}</TableCell>
                <TableCell className={isRTL ? 'text-right' : ''}>
                  <Badge className={sourceLabels[source]?.color || 'bg-gray-100'}>
                    {sourceLabels[source]?.label || source}
                  </Badge>
                </TableCell>
                <TableCell className={`text-muted-foreground ${isRTL ? 'text-right' : ''}`}>{format(new Date(invoice.issue_date), 'MMM d, yyyy')}</TableCell>
                <TableCell className={`text-muted-foreground ${isRTL ? 'text-right' : ''}`}>{invoice.due_date ? format(new Date(invoice.due_date), 'MMM d, yyyy') : '-'}</TableCell>
                <TableCell className={`font-mono font-semibold ${isRTL ? 'text-right' : ''}`}><CurrencyValue value={invoice.total?.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} size={14} /></TableCell>
                <TableCell><Badge className={statusColors[invoice.status]}>{statusLabels[invoice.status] || invoice.status}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => {
                        setSelectedInvoice(invoice);
                        setDocumentViewerOpen(true);
                      }}>
                        <Eye className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {isRTL ? 'عرض' : 'View'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => {
                        setSelectedInvoice(invoice);
                        setDocumentViewerOpen(true);
                      }}>
                        <Printer className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {isRTL ? 'طباعة' : 'Print'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => {
                        setSelectedInvoice(invoice);
                        setDocumentViewerOpen(true);
                      }}>
                        <Mail className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {isRTL ? 'بريد إلكتروني' : 'Email'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {invoice.source === 'project' && invoice.reference_id && (
                        <DropdownMenuItem onClick={() => window.location.href = `/projects/${invoice.reference_id}`}>
                          <FileText className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {isRTL ? 'عرض المشروع' : 'View Project'}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => updateStatus(invoice.id, 'sent')}>
                        <Send className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {isRTL ? 'تحديد كمرسلة' : 'Mark Sent'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateStatus(invoice.id, 'paid')}>
                        <CheckCircle className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {isRTL ? 'تحديد كمدفوعة' : 'Mark Paid'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(invoice.id)}>
                        <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
              );
            })}
            {filteredInvoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="h-32 text-center">
                  <Receipt className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">{isRTL ? 'لا توجد فواتير' : 'No invoices found'}</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className={`max-w-2xl ${isRTL ? 'text-right' : ''}`}>
          <DialogHeader><DialogTitle>{isRTL ? 'فاتورة جديدة' : 'New Invoice'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.invoices.client')}</Label>
                <Select value={formData.client_id || ''} onValueChange={v => setFormData({...formData, client_id: v})}>
                  <SelectTrigger><SelectValue placeholder={isRTL ? 'اختر العميل' : 'Select client'} /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('pages.invoices.dueDate')}</Label>
                <Input type="date" value={formData.due_date || ''} onChange={e => setFormData({...formData, due_date: e.target.value})} />
              </div>
            </div>

            <div className="space-y-2">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Label>{isRTL ? 'بنود الفاتورة' : 'Line Items'}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} className={`${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Plus className={`h-3 w-3 ${isRTL ? 'ml-1' : 'mr-1'}`} /> {isRTL ? 'إضافة بند' : 'Add Item'}
                </Button>
              </div>
              <div className="space-y-2">
                {(formData.items || []).map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-6" placeholder={isRTL ? 'الوصف' : 'Description'} value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} />
                    <Input className="col-span-2" type="number" placeholder={isRTL ? 'الكمية' : 'Qty'} value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} />
                    <Input className="col-span-3" type="number" placeholder={isRTL ? 'السعر' : 'Price'} value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
                    <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeItem(idx)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'نسبة الضريبة (%)' : 'Tax Rate (%)'}</Label>
              <Input type="number" className="w-32" value={formData.tax_rate || 0} onChange={e => setFormData({...formData, tax_rate: parseFloat(e.target.value) || 0})} />
            </div>

            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className={`flex justify-between text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span>{isRTL ? 'المجموع الفرعي' : 'Subtotal'}</span>
                <span className="font-mono">{currencySymbol} {totals.subtotal.toFixed(2)}</span>
              </div>
              <div className={`flex justify-between text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span>{isRTL ? 'الضريبة' : 'Tax'} ({formData.tax_rate || 0}%)</span>
                <span className="font-mono">{currencySymbol} {totals.tax.toFixed(2)}</span>
              </div>
              <div className={`flex justify-between font-bold text-lg border-t pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span>{t('pages.common.total')}</span>
                <span className="font-mono">{currencySymbol} {totals.total.toFixed(2)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} placeholder={isRTL ? 'شروط الدفع، رسالة شكر...' : 'Payment terms, thank you note...'} className={isRTL ? 'text-right' : ''} />
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('pages.common.cancel')}</Button>
            <Button onClick={handleSave}>{t('pages.invoices.createInvoice')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Viewer for Print/Email */}
      <DocumentViewer
        open={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        document={selectedInvoice}
        type="invoice"
        template={template}
        onEmailSent={() => fetchInvoices()}
      />
    </div>
  );
};

export default Invoices;
